/**
 * The outline of every shape the shapes tool draws (SRS C4).
 *
 * Pure maths in canvas coordinates: a drag from one corner to another comes in, a
 * list of points comes out. Nothing here knows about a canvas, a board or a pointer,
 * which is what lets "a rectangle has square corners at every zoom" be something a
 * test can prove.
 *
 * Every outline is a plain list of points, exactly like a stroke's. That is what lets
 * the eraser and the select tool work on a shape without knowing it is one.
 */

import type { Point, ShapeKind } from '@opal/shared';

/** How long an arrowhead is, as a share of the shaft. Scale-free, so resizing keeps it. */
export const ARROW_HEAD_RATIO = 0.22;

/** How wide the arrowhead opens, in radians — a little over 25°. */
export const ARROW_HEAD_ANGLE = 0.45;

/**
 * How many straight pieces a circle is drawn from.
 *
 * High enough that the edge still reads as curved at 800% zoom, where a shape drawn
 * at 100% is eight times the size it was drawn at.
 */
export const CIRCLE_SEGMENTS = 96;

/** SRS C4 — how many sides a polygon has unless the teacher says otherwise. */
export const DEFAULT_POLYGON_SIDES = 5;
export const MIN_POLYGON_SIDES = 3;
export const MAX_POLYGON_SIDES = 12;

/** The box two dragged corners describe, whichever way round they were dragged. */
export interface Box {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

export function boxOf(from: Point, to: Point): Box {
  return {
    left: Math.min(from.x, to.x),
    top: Math.min(from.y, to.y),
    right: Math.max(from.x, to.x),
    bottom: Math.max(from.y, to.y),
  };
}

/** A straight line from where the hand went down to where it is now. */
export function lineOutline(from: Point, to: Point): Point[] {
  return [from, to];
}

/**
 * A line with a head on the end the hand finished at.
 *
 * Drawn as one unbroken path — out to the tip, down one barb, back to the tip, down
 * the other — so that the whole arrow is a single thing to select, move or rub out.
 */
export function arrowOutline(from: Point, to: Point): Point[] {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy);
  if (length < 1e-6) return [from, to];

  const angle = Math.atan2(dy, dx);
  const head = length * ARROW_HEAD_RATIO;
  const barb = (turn: number): Point => ({
    x: to.x - head * Math.cos(angle + turn),
    y: to.y - head * Math.sin(angle + turn),
  });

  return [from, to, barb(ARROW_HEAD_ANGLE), to, barb(-ARROW_HEAD_ANGLE)];
}

/** Four corners, closed back to where it started. */
export function rectangleOutline(box: Box): Point[] {
  return [
    { x: box.left, y: box.top },
    { x: box.right, y: box.top },
    { x: box.right, y: box.bottom },
    { x: box.left, y: box.bottom },
    { x: box.left, y: box.top },
  ];
}

/** The oval that fits the box exactly. A square drag gives a circle. */
export function ellipseOutline(box: Box, segments = CIRCLE_SEGMENTS): Point[] {
  const cx = (box.left + box.right) / 2;
  const cy = (box.top + box.bottom) / 2;
  const rx = (box.right - box.left) / 2;
  const ry = (box.bottom - box.top) / 2;

  const points: Point[] = [];
  for (let step = 0; step <= segments; step += 1) {
    const angle = (step / segments) * Math.PI * 2 - Math.PI / 2;
    points.push({ x: cx + rx * Math.cos(angle), y: cy + ry * Math.sin(angle) });
  }
  return points;
}

/** Apex at the top middle, base along the bottom of the box. */
export function triangleOutline(box: Box): Point[] {
  const apex: Point = { x: (box.left + box.right) / 2, y: box.top };
  return [
    apex,
    { x: box.right, y: box.bottom },
    { x: box.left, y: box.bottom },
    apex,
  ];
}

/**
 * A regular polygon with one corner pointing straight up, sitting inside the box.
 *
 * Regular rather than free-form on purpose: every other shape here is made with one
 * drag, and a pentagon or a hexagon drawn the same way is what a maths lesson asks
 * for. The number of sides is the teacher's to choose.
 */
export function polygonOutline(box: Box, sides: number): Point[] {
  const count = Math.min(Math.max(Math.round(sides), MIN_POLYGON_SIDES), MAX_POLYGON_SIDES);
  const cx = (box.left + box.right) / 2;
  const cy = (box.top + box.bottom) / 2;
  const rx = (box.right - box.left) / 2;
  const ry = (box.bottom - box.top) / 2;

  const points: Point[] = [];
  for (let corner = 0; corner <= count; corner += 1) {
    const angle = (corner / count) * Math.PI * 2 - Math.PI / 2;
    points.push({ x: cx + rx * Math.cos(angle), y: cy + ry * Math.sin(angle) });
  }
  return points;
}

/** The outline of one shape, from the two corners the hand dragged between. */
export function outlineFor(
  kind: ShapeKind,
  from: Point,
  to: Point,
  sides = DEFAULT_POLYGON_SIDES,
): Point[] {
  if (kind === 'line') return lineOutline(from, to);
  if (kind === 'arrow') return arrowOutline(from, to);

  const box = boxOf(from, to);
  if (kind === 'rectangle') return rectangleOutline(box);
  if (kind === 'circle') return ellipseOutline(box);
  if (kind === 'triangle') return triangleOutline(box);
  return polygonOutline(box, sides);
}

/**
 * Whether a drag is big enough to be a shape at all.
 *
 * A tap that never moved is not a shape, and leaving a dot on the board every time a
 * teacher touches it with the shapes tool in hand would be a nuisance to rub out.
 */
export function isDrawable(from: Point, to: Point): boolean {
  return Math.hypot(to.x - from.x, to.y - from.y) > 1e-6;
}
